/**
 * How long is left of an impersonation window, rendered for a banner.
 *
 * A pure function in its own module for the reason `capabilities.ts` gives:
 * the decision is testable without a component, a store or a clock in the room,
 * and the banner is then only responsible for drawing what it returns.
 */

/**
 * Minutes remaining, rounded DOWN, or null when the input is unusable.
 *
 * Rounded down so the number never overstates what is left: "1 minute" that is
 * really 20 seconds is a promise the window cannot keep, and an operator part
 * way through a change is exactly who would rely on it.
 *
 * Returns null — rather than 0 — for a missing or unparseable timestamp, so a
 * caller can tell "no window" apart from "the window is over". Both hide the
 * banner, but only one of them means something went wrong.
 */
export function minutesRemaining(
  expiresAt: string | null,
  now: Date = new Date(),
): number | null {
  if (expiresAt === null) return null
  const end = Date.parse(expiresAt)
  if (Number.isNaN(end)) return null
  return Math.max(0, Math.floor((end - now.getTime()) / 60_000))
}

/**
 * The phrase the banner says, e.g. "3 h 12 min left".
 *
 * Hours are shown only once there is at least one, so a window in its last
 * stretch reads "12 min left" rather than "0 h 12 min left". Under a minute it
 * says "less than a minute left" instead of "0 min": a countdown that sits at
 * zero while still working reads as broken.
 */
export function formatRemaining(minutes: number | null): string | null {
  if (minutes === null) return null
  if (minutes < 1) return 'less than a minute left'
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) return `${rest} min left`
  return `${hours} h ${rest} min left`
}
